import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Briefcase, Settings as SettingsIcon,
  LogOut, PanelLeftClose, PanelLeftOpen, FlaskConical, Target, Scale, Gauge, CalendarRange, Receipt,
} from 'lucide-react';
import { api } from '../api';
import { useSession, CAP } from '../auth/session';

const LINKS = [
  { to: '/',         label: 'Dashboard', icon: LayoutDashboard, end: true,  caps: [CAP.VIEW_KPI], section: '' },
  { to: '/projects', label: 'Projects',  icon: Briefcase,       end: false, caps: [CAP.VIEW_PROJECTS], section: '' },
  { to: '/recon',    label: 'Overtime recon', icon: Scale,      end: false, caps: [CAP.VIEW_RECON], section: 'Operations' },
  { to: '/revenue',  label: 'Revenue & AR', icon: Receipt,      end: false, caps: [CAP.VIEW_PROJECT_PROFIT], section: 'Operations' },
  { to: '/utilisation', label: 'Utilisation', icon: Gauge,      end: false, caps: [CAP.VIEW_KPI], section: 'Reporting' },
  { to: '/schedule', label: 'Schedule', icon: CalendarRange,    end: false, caps: [CAP.VIEW_KPI], section: 'Reporting' },
  { to: '/cac',      label: 'Acquisition', icon: Target,        end: false, caps: [CAP.VIEW_KPI], section: 'Reporting' },
  { to: '/sandbox',  label: 'Sandbox',   icon: FlaskConical,    end: false, caps: [CAP.VIEW_KPI], section: 'Build' },
  { to: '/settings', label: 'Settings',  icon: SettingsIcon,    end: false,
    caps: [CAP.EDIT_RATE_CARD, CAP.EDIT_CTC, CAP.EDIT_OVERHEAD], anyOf: true, section: 'System' },
];

// preserve declaration order of sections
const SECTION_ORDER = ['', 'Operations', 'Reporting', 'Build', 'System'];

export default function Nav() {
  const { user, reload } = useSession();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('p45_nav_collapsed') === '1'
  );

  if (!user) return null;

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('p45_nav_collapsed', next ? '1' : '0');
      return next;
    });
  };

  const visible = LINKS.filter((l) => {
    if (l.leadership) return user.role === 'leadership';
    return l.anyOf
      ? l.caps.some((c) => user.capabilities.has(c))
      : l.caps.every((c) => user.capabilities.has(c));
  });

  const logout = async (e) => {
    e.preventDefault();
    await api.auth.logout();
    await reload();
  };

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-60'} min-h-screen flex flex-col nav-sidebar transition-[width] duration-200 ease-out`}
    >
      <div className={`${collapsed ? 'px-0 items-center' : 'px-6'} py-6 nav-border-b flex flex-col`}>
        <div className={`flex items-baseline gap-2 ${collapsed ? 'justify-center' : ''}`}>
          <span className="text-white text-xl font-bold tracking-tight">P45</span>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#e92030' }} />
        </div>
        {!collapsed && (
          <>
            <p className="text-[11px] uppercase tracking-[0.14em] nav-label mt-3">Commercial Silo</p>
            <p className="text-xs nav-label mt-0.5">internal dashboard</p>
          </>
        )}
      </div>

      <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} py-4`}>
        {SECTION_ORDER.map((section) => {
          const items = visible.filter((l) => (l.section || '') === section);
          if (!items.length) return null;
          return (
            <div key={section || 'main'} className="mb-3 space-y-0.5">
              {section && !collapsed && (
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] nav-label">{section}</p>
              )}
              {items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'} py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive ? 'nav-active' : 'nav-link'
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className={`${collapsed ? 'px-2 items-center' : 'px-6'} py-4 nav-border-t flex flex-col gap-2`}>
        <button
          onClick={toggle}
          title={collapsed ? 'Expand' : 'Collapse'}
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} text-[11px] nav-link hover:text-white transition-colors`}
        >
          {collapsed
            ? <PanelLeftOpen className="w-4 h-4" strokeWidth={1.75} />
            : <><PanelLeftClose className="w-4 h-4" strokeWidth={1.75} /> Collapse</>}
        </button>

        {!collapsed && (
          <>
            <p className="text-[11px] nav-label uppercase tracking-[0.12em] mt-1">{user.role}</p>
            <p className="text-[11px] nav-label truncate" title={user.email}>{user.email}</p>
          </>
        )}

        <button
          onClick={logout}
          title={collapsed ? 'Sign out' : undefined}
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} text-[11px] nav-link hover:text-white transition-colors`}
        >
          <LogOut className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
          {!collapsed && 'Sign out'}
        </button>
      </div>
    </aside>
  );
}
